import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
    CheckCircle,
    Image as ImageIcon,
    ArrowLeft,
    Confetti,
    PaperPlaneTilt,
} from "@phosphor-icons/react";
import { fetchPublicSuccessPage } from "@/lib/api";

const FALLBACK = {
    headline_main: "Du bist drin",
    headline_accent: "Welcome to Kylie's World.",
    subtext: "Ich hab dir gerade dein Foto an {email} geschickt. Es kann ein paar Minuten dauern bis es ankommt — schau auch im Spam-Ordner nach.",
    gift_card_eyebrow: "dein foto · exklusiv",
    gift_card_title: "Gratis Foto von Kylie West",
    gift_card_subtitle: "exklusiv von meinem privaten shoot",
    download_button_text: "Ansehen",
    download_url: "https://u.pcloud.link/publink/show?code=XZJNUO5ZzurHUuTQghFPMM3isRpQgSvD8oe7",
    next_card_1_title: "Check deine Inbox",
    next_card_1_body: "Eine Kopie ist gerade unterwegs. Schau im Posteingang oder Spam-Ordner.",
    next_card_2_title: "Bleib dran",
    next_card_2_body: "Neue Drops und exklusive Inhalte landen jede Woche in deiner Inbox.",
};

export default function Success() {
    const location = useLocation();
    const email = location.state?.email || "deine inbox";
    const [content, setContent] = useState(FALLBACK);

    useEffect(() => {
        fetchPublicSuccessPage()
            .then((data) => setContent({ ...FALLBACK, ...data }))
            .catch(() => {});
    }, []);

    const downloadUrl = location.state?.downloadUrl || content.download_url;
    const subtext = (content.subtext || "").split("{email}");

    return (
        <div className="relative min-h-screen overflow-hidden" data-testid="success-page">
            <div className="pointer-events-none absolute left-1/2 top-1/3 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#FF5A00]/15 blur-[140px]" />
            <div className="grain-overlay" />

            <nav className="relative z-10 flex items-center justify-between px-6 py-6 sm:px-10">
                <Link to="/" className="flex items-center gap-2 text-white/60 transition hover:text-white" data-testid="back-home-link">
                    <ArrowLeft size={16} weight="bold" />
                    <span className="font-mono text-xs uppercase tracking-[0.25em]">zurück</span>
                </Link>
                <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-[#FF5A00]" />
                    <span className="font-mono text-xs uppercase tracking-[0.25em] text-white/60">kylie.west</span>
                </div>
            </nav>

            <div className="relative z-10 mx-auto flex max-w-3xl flex-col items-center px-6 pb-16 pt-8 sm:px-10 sm:pt-16">
                <motion.div
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 200, damping: 18 }}
                    className="relative mb-8"
                >
                    <div className="absolute inset-0 rounded-full bg-[#FF5A00] blur-2xl opacity-50" />
                    <div className="relative flex h-24 w-24 items-center justify-center rounded-full bg-[#FF5A00] text-black">
                        <CheckCircle size={56} weight="fill" />
                    </div>
                </motion.div>

                <motion.div
                    initial={{ y: 24, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.6 }}
                    className="text-center"
                >
                    <div className="label-eyebrow mb-4 flex items-center justify-center gap-2 text-[#FF5A00]">
                        <Confetti size={14} weight="fill" />
                        geschafft
                    </div>
                    <h1 className="font-display text-4xl font-black leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
                        {content.headline_main} <br />
                        <span className="text-[#FF5A00]">{content.headline_accent}</span>
                    </h1>
                    <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-white/60 sm:text-lg">
                        {subtext[0]}
                        {subtext.length > 1 && (
                            <>
                                <span className="font-mono text-white">{email}</span>
                                {subtext.slice(1).join("{email}")}
                            </>
                        )}
                    </p>
                </motion.div>

                {/* Gift Card */}
                <motion.div
                    initial={{ y: 24, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.4, duration: 0.6 }}
                    className="bento-card mt-12 w-full overflow-hidden p-6 sm:p-8"
                    data-testid="gift-card"
                >
                    <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
                        <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#FF5A00] to-[#FF7A33] glow-orange">
                            <ImageIcon size={36} weight="duotone" className="text-black" />
                        </div>
                        <div className="flex-1">
                            <div className="label-eyebrow mb-1 text-[#FF5A00]">{content.gift_card_eyebrow}</div>
                            <h3 className="font-display text-xl font-bold text-white sm:text-2xl">
                                {content.gift_card_title}
                            </h3>
                            <p className="mt-1 text-sm text-white/50">
                                {content.gift_card_subtitle}
                            </p>
                        </div>
                        <a
                            href={downloadUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FF5A00] px-6 py-3 font-bold text-black transition-all duration-300 hover:-translate-y-1 hover:bg-[#FF7A33] hover:shadow-[0_8px_32px_rgba(255,90,0,0.35)] active:scale-95"
                            data-testid="download-gift-button"
                        >
                            <ImageIcon size={18} weight="duotone" />
                            {content.download_button_text}
                        </a>
                    </div>
                </motion.div>

                {/* What's next */}
                <motion.div
                    initial={{ y: 24, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.55, duration: 0.6 }}
                    className="mt-6 grid w-full grid-cols-1 gap-4 sm:grid-cols-2"
                >
                    <div className="bento-card p-6">
                        <PaperPlaneTilt size={24} weight="duotone" className="text-[#FF5A00]" />
                        <h4 className="mt-3 font-display text-lg font-bold text-white">{content.next_card_1_title}</h4>
                        <p className="mt-1 text-sm text-white/50">
                            {content.next_card_1_body}
                        </p>
                    </div>
                    <div className="bento-card p-6">
                        <Confetti size={24} weight="duotone" className="text-[#FF5A00]" />
                        <h4 className="mt-3 font-display text-lg font-bold text-white">{content.next_card_2_title}</h4>
                        <p className="mt-1 text-sm text-white/50">
                            {content.next_card_2_body}
                        </p>
                    </div>
                </motion.div>

                <Link
                    to="/"
                    className="mt-12 font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 transition hover:text-white"
                    data-testid="return-home-link"
                >
                    ← zurück zur startseite
                </Link>
            </div>
        </div>
    );
}
